import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Bank, ArrowRight, CheckCircle, X as XIcon, Info, ArrowsSplit,
  ArrowDown, ArrowUp, Plus, Warning,
} from "@phosphor-icons/react";
import client from "../lib/api";

const money = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * QuickBooks payroll → journal-entry sync bar for the Payroll page.
 *
 * SIMPLE mode (default): Debit Payroll Expense (gross) / Credit Bank (gross).
 * SPLIT mode: Debit Payroll Expense (gross) / Credit Bank (net_pay) /
 *             Credit Payroll Liabilities × N (one line per non-zero deduction
 *             category with memo).
 *
 * Auto-hides when QBO isn't configured or the user hasn't connected.
 */
export default function PayrollQboSyncBar({ periodStart, periodEnd, onSynced } = {}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [creatingLiab, setCreatingLiab] = useState(false);
  const [split, setSplit] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!periodStart || !periodEnd) return;
    setLoading(true);
    try {
      const { data } = await client.get("/payroll/qbo-sync/preview", {
        params: { period_start: periodStart, period_end: periodEnd },
      });
      setPreview(data);
    } catch {/* silent — hide the bar if preview fails */}
    finally { setLoading(false); }
  }, [periodStart, periodEnd]);

  useEffect(() => { load(); }, [load]);

  const totals = preview?.totals || {};
  const already = preview?.already_synced === true;
  const simpleJe = preview?.journal_entry_preview || {};
  const splitJe = preview?.journal_entry_preview_split || {};
  const accounts = preview?.accounts || {};
  const hasDeductions = preview?.has_non_zero_deductions === true;
  const splitReady = preview?.split_ready === true;
  const missingLiabAccount = split && hasDeductions && !accounts.liability;

  // Balance sanity check for split mode preview (should always be true when
  // split_ready === true, but we compute for the UI badge).
  const splitBalanced = useMemo(() => {
    if (!splitJe.debit_lines || !splitJe.credit_lines) return false;
    const d = splitJe.debit_lines.reduce((s, x) => s + Number(x.amount || 0), 0);
    const c = splitJe.credit_lines.reduce((s, x) => s + Number(x.amount || 0), 0);
    return Math.abs(d - c) < 0.01;
  }, [splitJe]);

  if (loading && !preview) return null;
  if (!preview) return null;
  if (preview.configured === false || preview.connected === false) return null;

  const currentGross = split ? splitJe.gross_pay : simpleJe.gross_pay;

  const doCreateLiability = async () => {
    setCreatingLiab(true);
    try {
      const { data } = await client.post("/payroll/qbo-sync/create-liability-account");
      toast.success(
        data.status === "already_exists"
          ? `Using existing "${data.account.name}"`
          : `Created "${data.account.name}" in QuickBooks`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create account");
    } finally { setCreatingLiab(false); }
  };

  const doSync = async () => {
    if (split && missingLiabAccount) {
      toast.error("Create the Payroll Liabilities account first");
      return;
    }
    const modeText = split ? "split (multi-line)" : "simple (2-line)";
    if (!window.confirm(
      `Push ${money(currentGross)} payroll journal entry to QuickBooks for ${periodStart} → ${periodEnd}?\n\nMode: ${modeText}`,
    )) return;
    setSyncing(true);
    try {
      const { data } = await client.post("/payroll/qbo-sync", null, {
        params: { period_start: periodStart, period_end: periodEnd, split },
        timeout: 60000,
      });
      setResult(data);
      toast.success(`Payroll synced — JE #${data.qbo_id} (${data.line_count} lines)`);
      await load();
      onSynced?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <>
      <div
        className="card-surface p-3 mb-4 flex flex-col gap-2"
        data-testid="payroll-qbo-sync-bar"
      >
        {/* Row 1: identity + summary + primary CTA */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              already ? "bg-emerald-500/15 text-emerald-400" : "bg-[#FF5A1F]/15 text-[#FF9764]"
            }`}>
              <Bank size={16} weight="fill" />
            </div>
            <div>
              <div className="text-xs font-semibold text-white leading-tight">
                QuickBooks payroll JE
              </div>
              <div className="text-[10px] text-neutral-500 leading-tight">
                {already ? `Already synced · QBO #${preview.already_qbo_id}` : "Push as journal entry"}
              </div>
            </div>
          </div>

          <div className="flex-1 flex items-center gap-3 flex-wrap text-xs">
            <span className="text-neutral-300">
              <b className="text-white" data-testid="payroll-qbo-gross">{money(currentGross)}</b> gross
            </span>
            <span className="text-neutral-600">·</span>
            <span className="text-neutral-500">{totals.driver_count || 0} drivers</span>
            <span className="text-neutral-600">·</span>
            <span className="text-neutral-500">{totals.load_count || 0} loads</span>
            {split && hasDeductions && (
              <>
                <span className="text-neutral-600">·</span>
                <span className="text-neutral-300">
                  <b className="text-emerald-300">{money(splitJe.net_pay)}</b> net paid
                </span>
                <span className="text-neutral-600">·</span>
                <span className="text-neutral-300">
                  <b className="text-amber-300">{money(splitJe.deductions_total)}</b> withheld
                </span>
              </>
            )}
          </div>

          {already ? (
            <span
              className="text-xs rounded-full px-3 py-1.5 border border-emerald-500/40 text-emerald-300 bg-emerald-500/10 inline-flex items-center gap-1.5"
              data-testid="payroll-qbo-already-synced"
            >
              <CheckCircle size={11} weight="fill" /> Synced
            </span>
          ) : (
            <button
              onClick={doSync}
              disabled={syncing || (currentGross || 0) <= 0 || (split && missingLiabAccount)}
              data-testid="payroll-qbo-sync-btn"
              className={`text-xs rounded-full px-4 py-2 inline-flex items-center gap-2 transition-colors ${
                (currentGross || 0) <= 0 || (split && missingLiabAccount)
                  ? "border border-white/10 text-neutral-600 cursor-not-allowed"
                  : "bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
              } disabled:opacity-60`}
            >
              {syncing ? (
                <>
                  <div className="w-3 h-3 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                  Syncing...
                </>
              ) : (currentGross || 0) <= 0 ? (
                "Nothing to sync"
              ) : (
                <>
                  Push to QuickBooks <ArrowRight size={11} weight="bold" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Row 2: split toggle + expand details toggle */}
        {!already && (
          <div className="flex items-center gap-3 flex-wrap text-[11px] border-t border-white/5 pt-2">
            <label
              className={`inline-flex items-center gap-2 cursor-pointer select-none rounded-full px-2.5 py-1 border ${
                split
                  ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/10 text-[#FF9764]"
                  : "border-white/10 text-neutral-400 hover:border-white/20"
              }`}
              data-testid="payroll-qbo-split-toggle"
            >
              <input
                type="checkbox"
                checked={split}
                onChange={(e) => setSplit(e.target.checked)}
                className="peer sr-only"
              />
              <span className="w-7 h-3.5 rounded-full bg-neutral-700 peer-checked:bg-[#FF5A1F] relative inline-block transition-colors">
                <span className={`absolute top-0.5 left-0.5 w-2.5 h-2.5 rounded-full bg-white transition-transform ${split ? "translate-x-3.5" : ""}`} />
              </span>
              <ArrowsSplit size={11} weight="bold" />
              <span className="font-semibold">Split into liability lines</span>
            </label>

            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1"
              data-testid="payroll-qbo-details-toggle"
            >
              {showDetails ? "Hide" : "Preview"} JE lines
            </button>

            {split && !hasDeductions && (
              <span className="text-neutral-500 italic">
                No deductions this period — split will use 2 lines.
              </span>
            )}
            {split && missingLiabAccount && (
              <button
                type="button"
                onClick={doCreateLiability}
                disabled={creatingLiab}
                data-testid="payroll-qbo-create-liab-btn"
                className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1 border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 disabled:opacity-60"
              >
                {creatingLiab ? (
                  <div className="w-3 h-3 rounded-full border-2 border-amber-500/40 border-t-amber-300 animate-spin" />
                ) : (
                  <Plus size={11} weight="bold" />
                )}
                Create Payroll Liabilities account
              </button>
            )}
            {split && splitReady && hasDeductions && (
              <span className="ml-auto inline-flex items-center gap-1 text-emerald-400">
                <CheckCircle size={11} weight="fill" />
                Ready · {splitJe.credit_lines?.length || 0} credit lines · balanced
              </span>
            )}
          </div>
        )}

        {/* Row 3: expanded JE line preview */}
        {showDetails && !already && (
          <div
            className="mt-1 rounded-lg border border-white/10 bg-black/40 p-3 text-[11px] space-y-2"
            data-testid="payroll-qbo-je-preview"
          >
            <div className="text-neutral-500 uppercase tracking-widest text-[10px]">
              {split ? "Split journal entry preview" : "Simple journal entry preview"}
            </div>
            {(split ? splitJe.debit_lines : simpleJe.gross_pay > 0 ? [{
              account_name: accounts.expense?.name || "Payroll Expense",
              amount: simpleJe.gross_pay,
              memo: `Driver gross pay · ${totals.driver_count} drivers · ${totals.load_count} loads`,
            }] : []).map((line, i) => (
              <div key={`d-${i}`} className="grid grid-cols-[16px_1fr_auto] gap-2 items-center">
                <ArrowDown size={12} className="text-rose-400" weight="bold" />
                <div className="min-w-0">
                  <div className="text-white truncate">
                    <b>Debit</b> · {line.account_name || <span className="text-amber-400">(missing account)</span>}
                  </div>
                  <div className="text-neutral-500 truncate">{line.memo}</div>
                </div>
                <div className="text-white font-mono text-right">{money(line.amount)}</div>
              </div>
            ))}
            {(split ? splitJe.credit_lines : simpleJe.gross_pay > 0 ? [{
              account_name: accounts.bank?.name || "Bank",
              amount: simpleJe.gross_pay,
              memo: `Payroll draw from ${accounts.bank?.name || "Bank"}`,
            }] : []).map((line, i) => (
              <div key={`c-${i}`} className="grid grid-cols-[16px_1fr_auto] gap-2 items-center">
                <ArrowUp size={12} className="text-emerald-400" weight="bold" />
                <div className="min-w-0">
                  <div className="text-white truncate">
                    <b>Credit</b> · {line.account_name || <span className="text-amber-400">(missing account)</span>}
                  </div>
                  <div className="text-neutral-500 truncate">{line.memo}</div>
                </div>
                <div className="text-white font-mono text-right">{money(line.amount)}</div>
              </div>
            ))}
            {split && !splitBalanced && (
              <div className="flex items-center gap-1.5 text-amber-400 pt-1 border-t border-white/5">
                <Warning size={11} weight="fill" />
                Preview unbalanced — usually means the liability account is missing.
              </div>
            )}
          </div>
        )}
      </div>

      {result && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
          data-testid="payroll-qbo-sync-result-modal"
        >
          <div className="min-h-full flex items-start justify-center py-8">
            <div className="w-full max-w-md bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-400 font-semibold mb-1">
                    <CheckCircle size={12} weight="fill" /> Synced
                  </div>
                  <div className="text-lg font-semibold text-white">
                    Payroll journal entry {result.split_mode ? "(split)" : ""}
                  </div>
                </div>
                <button
                  onClick={() => setResult(null)}
                  className="text-neutral-500 hover:text-white"
                  data-testid="payroll-qbo-result-close"
                >
                  <XIcon size={18} />
                </button>
              </div>
              <div className="p-5 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-neutral-500">QuickBooks JE ID</div>
                  <div className="text-white font-mono">#{result.qbo_id}</div>
                  <div className="text-neutral-500">Amount (gross)</div>
                  <div className="text-white font-mono">{money(result.gross)}</div>
                  <div className="text-neutral-500">Line count</div>
                  <div className="text-white font-mono">{result.line_count || 2}</div>
                  <div className="text-neutral-500">Mode</div>
                  <div className="text-white">
                    {result.split_mode ? "Split (multi-line)" : "Simple (2-line)"}
                  </div>
                  <div className="text-neutral-500">Debit account</div>
                  <div className="text-white">{result.expense_account || "—"}</div>
                  <div className="text-neutral-500">Bank (credit)</div>
                  <div className="text-white">{result.bank_account || "—"}</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[11px] text-neutral-400 flex items-start gap-2">
                  <Info size={12} className="flex-shrink-0 mt-0.5" />
                  <span>
                    {result.split_mode ? (
                      <>
                        Split mode credits <b className="text-neutral-200">net pay</b> to the bank and posts each
                        deduction category (fuel advances, insurance, escrow, chargebacks, truck/trailer lease,
                        other) as its own memo-tagged line against <b className="text-neutral-200">Payroll Liabilities</b>.
                        You can reclass in QuickBooks — the JE is unlocked for editing.
                      </>
                    ) : (
                      <>
                        Debit posts to <b className="text-neutral-200">{result.expense_account}</b>, credit posts to{" "}
                        <b className="text-neutral-200">{result.bank_account}</b>. Turn on <b>Split into liability lines</b>{" "}
                        next period to break out withholdings per category.
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
